<script setup>
/* global axios */
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AModal from './AModal.vue';
import ReminderEditor from './ReminderEditor.vue';
import { fmtMoney, ICON } from './constants';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const base = `/api/v1/accounts/${props.accountId}/kanban`;

const services = ref([]);
const show = ref(false);
const wizardStep = ref(1);
const form = ref({});
const reminders = ref([]);
const removedReminders = ref([]);
const waInboxes = ref([]);
// cache de templates aprovados por caixa (cada lembrete tem sua própria caixa)
const templatesByInbox = ref({});

const isOfficial = inboxId => {
  const i = waInboxes.value.find(x => x.id === inboxId);
  return (i?.provider || '') === 'whatsapp_cloud';
};
// carrega (e cacheia) os templates aprovados de uma caixa oficial
const loadTemplatesFor = async inboxId => {
  if (!inboxId || !isOfficial(inboxId) || templatesByInbox.value[inboxId]) return;
  try {
    const { data } = await axios.get(`${base}/whatsapp_templates`, {
      params: { inbox_id: inboxId },
    });
    templatesByInbox.value = {
      ...templatesByInbox.value,
      [inboxId]: (data.data || []).filter(
        t => (t.status || '').toUpperCase() === 'APPROVED'
      ),
    };
  } catch (e) {
    templatesByInbox.value = { ...templatesByInbox.value, [inboxId]: [] };
  }
};

const load = async () => {
  services.value =
    (await axios.get(`${base}/appointment_services`)).data.data || [];
};
const loadWaInboxes = async () => {
  try {
    // lista TODAS as caixas (oficial + não-oficial/Evolution + demais); a aba de
    // template só aparece quando a caixa selecionada é WhatsApp Cloud oficial.
    waInboxes.value =
      (await axios.get(`/api/v1/accounts/${props.accountId}/inboxes`)).data
        .payload || [];
  } catch (e) {
    waInboxes.value = [];
  }
};
const open = async (s = null) => {
  wizardStep.value = 1;
  form.value = s
    ? { ...s }
    : {
        name: '',
        description: '',
        duration_minutes: 60,
        preparation_minutes: 0,
        color: '#10B981',
        default_price: '',
        is_online: true,
        is_active: true,
      };
  removedReminders.value = [];
  reminders.value = s
    ? (await axios.get(`${base}/appointment_services/${s.id}/reminders`)).data
        .data || []
    : [];
  // pré-carrega templates das caixas oficiais já usadas pelos lembretes existentes
  await Promise.all(
    [...new Set(reminders.value.map(r => r.inbox_id).filter(Boolean))].map(id =>
      loadTemplatesFor(id)
    )
  );
  show.value = true;
};
const addReminder = () =>
  reminders.value.push({
    label: '',
    timing: 'before',
    days_before: 1,
    hours_before: 0,
    minutes_before: 0,
    message: '',
    is_active: true,
    inbox_id: null,
    wa_template_name: '',
    wa_template_lang: '',
  });
const removeReminder = i => {
  const r = reminders.value[i];
  if (r.id) removedReminders.value.push(r.id);
  reminders.value.splice(i, 1);
};
const syncReminders = async serviceId => {
  const rbase = `${base}/appointment_services/${serviceId}/reminders`;
  await Promise.all(
    removedReminders.value.map(id =>
      axios.delete(`${rbase}/${id}`).catch(() => {})
    )
  );
  await Promise.all(
    reminders.value.map(r =>
      r.id ? axios.patch(`${rbase}/${r.id}`, r) : axios.post(rbase, r)
    )
  );
};
const save = async () => {
  if (!form.value.name?.trim()) return;
  let id = form.value.id;
  if (id) await axios.patch(`${base}/appointment_services/${id}`, form.value);
  else {
    id = (await axios.post(`${base}/appointment_services`, form.value)).data
      .data.id;
  }
  await syncReminders(id);
  show.value = false;
  await load();
};
const remove = async s => {
  if (
    !window.confirm(t('ATENDIMENTOS.services.deleteConfirm', { name: s.name }))
  )
    return;
  await axios.delete(`${base}/appointment_services/${s.id}`);
  await load();
};

onMounted(() => {
  load();
  loadWaInboxes();
});
</script>

<template>
  <div class="p-6">
    <div class="flex justify-end mb-4">
      <button
        class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
        @click="open()"
      >
        {{ t('ATENDIMENTOS.services.newService') }}
      </button>
    </div>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="s in services"
        :key="s.id"
        class="p-4 bg-n-solid-2 border rounded-xl border-n-weak"
      >
        <div class="flex items-start justify-between">
          <div class="flex items-center gap-2">
            <span
              class="w-3 h-3 rounded-full"
              :style="{ background: s.color }"
            /><span class="font-semibold text-n-slate-12">{{ s.name }}</span>
          </div>
          <button
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="open(s)"
          >
            {{ t('ATENDIMENTOS.services.edit') }}
          </button>
        </div>
        <p v-if="s.description" class="mt-1 text-sm text-n-slate-10">
          {{ s.description }}
        </p>
        <div class="flex gap-3 mt-2 text-xs text-n-slate-11">
          <span class="inline-flex items-center gap-1"
            ><svg
              class="w-3 h-3"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" :d="ICON.clock[0]" /></svg
            >{{
              t('ATENDIMENTOS.form.durationMinutes', {
                n: s.duration_minutes,
              })
            }}</span
          >
          <span v-if="s.default_price">{{ fmtMoney(s.default_price) }}</span>
        </div>
        <button
          class="mt-3 text-xs text-n-ruby-11 hover:underline"
          @click="remove(s)"
        >
          {{ t('ATENDIMENTOS.patients.delete') }}
        </button>
      </div>
      <p v-if="!services.length" class="text-n-slate-10">
        {{ t('ATENDIMENTOS.services.empty') }}
      </p>
    </div>

    <AModal
      v-if="show"
      :title="
        form.id
          ? t('ATENDIMENTOS.services.modal.editTitle')
          : t('ATENDIMENTOS.services.modal.newTitle')
      "
      size="lg"
      @close="show = false"
    >
      <!-- stepper -->
      <div class="flex items-center max-w-md mx-auto mb-4">
        <template v-for="(lbl, i) in [
          t('ATENDIMENTOS.services.modal.step1'),
          t('ATENDIMENTOS.services.modal.step2'),
        ]" :key="i">
          <button
            type="button"
            class="flex flex-col items-center gap-1.5"
            @click="i === 0 || form.name ? (wizardStep = i + 1) : null"
          >
            <div
              class="grid text-sm font-semibold transition-all rounded-full w-9 h-9 place-items-center"
              :class="
                wizardStep > i + 1
                  ? 'bg-n-brand text-white'
                  : wizardStep === i + 1
                    ? 'bg-n-brand text-white ring-4 ring-n-brand/20'
                    : 'bg-n-alpha-2 text-n-slate-10'
              "
            >
              <span
                v-if="wizardStep > i + 1"
                class="i-lucide-check size-4"
              />
              <span v-else>{{ i + 1 }}</span>
            </div>
            <span
              class="text-xs whitespace-nowrap"
              :class="
                wizardStep === i + 1
                  ? 'text-n-slate-12 font-medium'
                  : 'text-n-slate-10'
              "
              >{{ lbl }}</span
            >
          </button>
          <div
            v-if="i < 1"
            class="flex-1 h-0.5 mx-2 mb-5 rounded transition-colors"
            :class="wizardStep > i + 1 ? 'bg-n-brand' : 'bg-n-alpha-2'"
          />
        </template>
      </div>

      <!-- PASSO 1: configurações do serviço -->
      <div v-show="wizardStep === 1" class="space-y-3">
        <div class="flex gap-3">
          <div class="flex-1">
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('ATENDIMENTOS.services.modal.namefield')
            }}</label
            ><input
              v-model="form.name"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('ATENDIMENTOS.services.modal.color')
            }}</label
            ><input
              v-model="form.color"
              type="color"
              class="w-12 h-9 p-0.5 border rounded border-n-weak bg-n-background"
            />
          </div>
        </div>
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.services.modal.description')
          }}</label
          ><textarea
            v-model="form.description"
            rows="2"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div class="grid grid-cols-3 gap-3">
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('ATENDIMENTOS.services.modal.duration')
            }}</label
            ><input
              v-model="form.duration_minutes"
              type="number"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('ATENDIMENTOS.services.modal.preparation')
            }}</label
            ><input
              v-model="form.preparation_minutes"
              type="number"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('ATENDIMENTOS.services.modal.defaultPrice')
            }}</label
            ><input
              v-model="form.default_price"
              type="number"
              step="0.01"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              :placeholder="t('ATENDIMENTOS.form.pricePlaceholder')"
            />
          </div>
        </div>
        <label class="flex items-center gap-2 text-sm text-n-slate-12"
          ><input v-model="form.is_online" type="checkbox" />
          {{ t('ATENDIMENTOS.services.modal.availableOnline') }}</label
        >
      </div>

      <!-- PASSO 2: lembretes automáticos -->
      <div v-show="wizardStep === 2" class="space-y-2">
        <div class="flex items-center justify-between">
          <label class="block text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.services.reminders.title')
          }}</label>
          <button
            class="inline-flex items-center gap-1 text-xs font-medium text-n-blue-11 hover:underline"
            @click="addReminder"
          >
            <span class="i-lucide-plus size-3.5" />
            {{ t('ATENDIMENTOS.services.reminders.addReminder') }}
          </button>
        </div>
        <p class="text-[0.6875rem] text-n-slate-10">
          {{ t('ATENDIMENTOS.services.reminders.variables') }}
        </p>
        <p
          v-if="!reminders.length"
          class="py-6 text-center text-[0.8125rem] text-n-slate-10"
        >
          {{ t('ATENDIMENTOS.services.reminders.noReminders') }}
        </p>
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ReminderEditor
            v-for="(r, i) in reminders"
            :key="i"
            :reminder="r"
            :inboxes="waInboxes"
            :templates-by-inbox="templatesByInbox"
            @remove="removeReminder(i)"
            @load-templates="loadTemplatesFor"
          />
        </div>
      </div>

      <template #footer>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="show = false"
        >
          {{ t('ATENDIMENTOS.services.modal.cancel') }}
        </button>
        <button
          v-if="wizardStep === 2"
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="wizardStep = 1"
        >
          {{ t('ATENDIMENTOS.services.modal.back') }}
        </button>
        <button
          v-if="wizardStep === 1"
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
          :disabled="!form.name || !form.name.trim()"
          @click="wizardStep = 2"
        >
          {{ t('ATENDIMENTOS.services.modal.next') }}
          <span class="i-lucide-chevron-right size-4" />
        </button>
        <button
          v-else
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
          @click="save"
        >
          {{ t('ATENDIMENTOS.services.modal.save') }}
        </button>
      </template>
    </AModal>
  </div>
</template>
